import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Accordion,
  Alert,
  Badge,
  Button,
  Card,
  Form,
  InputGroup,
  OverlayTrigger,
  Spinner,
  Table,
  Tooltip,
} from "react-bootstrap";
import {
  Search,
  Plus,
  ExternalLink,
  ToggleLeft,
  ToggleRight,
  Save,
  Edit2,
  X,
} from "react-feather";
import {
  IKindredCouponDetail,
  IKindredCouponTargetingResponse,
  IKindredCouponTargetingRecord,
  IKindredCouponTargetingUpsert,
  IKindredCouponUpdate,
  IKindredMetadataUpdate,
  KindredCouponCoverage,
  ITargetingConditions,
  IBundleRequirement,
  DiscountApplication,
  IFingerprintSearchResult,
  IFingerprintSearchResponse,
  IFingerprintSearchRequest,
  ICategoryEntry,
  INetworkCategories,
  IManufacturerSearchResult,
} from "../../interfaces";
import { usePost, usePut, useGetLazy } from "../../api";
import { MetadataEditor } from "./entity-detail-panel";
import { AdvertiserPanel } from "./advertiser-panel";
import { buildProductTargetPayload } from "./product-target";
import { CouponWithStatus, ICouponTargetCountItem } from "./types";

// ── Field Label with tooltip ────────────────────────────

const FieldLabel: React.FC<{
  children: React.ReactNode;
  tip: string;
  className?: string;
}> = ({ children, tip, className = "small mb-0" }) => (
  <Form.Label className={className}>
    {children}
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip style={{ fontSize: 12 }}>{tip}</Tooltip>}
    >
      <span
        className="ms-1 text-muted"
        style={{ cursor: "help", fontSize: 11 }}
      >
        (?)
      </span>
    </OverlayTrigger>
  </Form.Label>
);

// ── Shared Target Search Panel ───────────────────────────

type TargetSearchType = "product" | "category" | "manufacturer";

const TargetSearchPanel: React.FC<{
  domain: string;
  defaultType?: TargetSearchType;
  className?: string;
  maxHeight?: number;
  currentValue?: string;
  onSelectCategory: (cat: ICategoryEntry) => void;
  onSelectManufacturer: (mfr: IManufacturerSearchResult) => void;
  onSelectProduct: (match: IFingerprintSearchResult) => void;
  onClose: () => void;
}> = ({
  domain,
  defaultType = "product",
  className = "border rounded p-2 bg-white",
  maxHeight = 160,
  currentValue,
  onSelectCategory,
  onSelectManufacturer,
  onSelectProduct,
  onClose,
}) => {
  const [searchType, setSearchType] = useState<TargetSearchType>(defaultType);
  const [query, setQuery] = useState("");
  const [catResults, setCatResults] = useState<ICategoryEntry[]>([]);
  const [catLoading, setCatLoading] = useState(false);
  const [mfrResults, setMfrResults] = useState<IManufacturerSearchResult[]>([]);
  const [mfrLoading, setMfrLoading] = useState(false);
  const [prodResults, setProdResults] = useState<IFingerprintSearchResult[]>(
    []
  );
  const [prodLoading, setProdLoading] = useState(false);

  const catSearch = useGetLazy<INetworkCategories>();
  const mfrSearch = useGetLazy<{
    manufacturers: IManufacturerSearchResult[];
  }>();
  const prodSearch = usePost<
    IFingerprintSearchResponse,
    IFingerprintSearchRequest
  >();

  const handleSearch = async () => {
    if (!query.trim()) return;
    if (searchType === "category") {
      setCatLoading(true);
      try {
        const result = await catSearch.execute(
          `affiliate-products/categories?network=Awin&domain=${encodeURIComponent(domain)}`
        );
        const all: ICategoryEntry[] = [
          ...(result?.categories || []),
          ...(result?.advertisers?.flatMap((a: any) => a.categories || []) ||
            []),
        ];
        const unique = new Map<string, ICategoryEntry>();
        all.forEach((c) => {
          if (!unique.has(c.category)) unique.set(c.category, c);
        });
        setCatResults(
          Array.from(unique.values())
            .filter((c) =>
              c.category.toLowerCase().includes(query.toLowerCase())
            )
            .slice(0, 20)
        );
      } finally {
        setCatLoading(false);
      }
    } else if (searchType === "manufacturer") {
      setMfrLoading(true);
      try {
        const result = await mfrSearch.execute(
          `affiliate-products/manufacturers?domain=${encodeURIComponent(domain)}&q=${encodeURIComponent(query)}&limit=20`
        );
        setMfrResults(result?.manufacturers || []);
      } finally {
        setMfrLoading(false);
      }
    } else {
      setProdLoading(true);
      try {
        const result = await prodSearch.execute(
          "affiliate-products/search/fingerprint",
          {
            productName: query,
            domain,
            minimumConfidence: 0.7,
            limit: 15,
            offset: 0,
          }
        );
        setProdResults(result?.results || []);
      } finally {
        setProdLoading(false);
      }
    }
  };

  const isLoading = catLoading || mfrLoading || prodLoading;

  return (
    <div className={className} style={{ minWidth: 0 }}>
      <div className="d-flex gap-1 mb-2">
        <Form.Select
          size="sm"
          value={searchType}
          style={{ width: 150, flexShrink: 0 }}
          onChange={(e) => {
            setSearchType(e.target.value as TargetSearchType);
            setCatResults([]);
            setMfrResults([]);
            setProdResults([]);
          }}
        >
          <option value="product">Product</option>
          <option value="category">Category</option>
          <option value="manufacturer">Manufacturer</option>
        </Form.Select>
        <Form.Control
          size="sm"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search…"
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
        />
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={handleSearch}
          disabled={isLoading || !query.trim()}
        >
          {isLoading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <Search size={12} />
          )}
        </Button>
        <Button size="sm" variant="outline-secondary" onClick={onClose}>
          <X size={12} />
        </Button>
      </div>

      {currentValue && (
        <div className="small text-muted mb-1">
          Current: <code className="text-dark">{currentValue}</code>
        </div>
      )}

      <div style={{ maxHeight, overflowY: "auto" }}>
        {searchType === "category" &&
          catResults.map((cat) => (
            <div
              key={cat.category}
              className="px-2 py-1 small border-bottom d-flex justify-content-between align-items-center"
              style={{ cursor: "pointer" }}
              onClick={() => onSelectCategory(cat)}
            >
              <span>{cat.category}</span>
              <Badge bg="secondary" className="ms-2">
                {cat.productCount}
              </Badge>
            </div>
          ))}

        {searchType === "manufacturer" &&
          mfrResults.map((mfr) => (
            <div
              key={mfr.manufacturerCanonical}
              className="px-2 py-1 small border-bottom d-flex justify-content-between align-items-center"
              style={{ cursor: "pointer" }}
              onClick={() => onSelectManufacturer(mfr)}
            >
              <span>
                {mfr.manufacturer}{" "}
                <span className="text-muted">
                  ({mfr.manufacturerCanonical})
                </span>
              </span>
              <Badge bg="secondary" className="ms-2">
                {mfr.productCount}
              </Badge>
            </div>
          ))}

        {searchType === "product" &&
          prodResults.map((match) => (
            <div
              key={`${match.product.id}-${match.matchedKey}`}
              className="px-2 py-1 small border-bottom"
              style={{ cursor: "pointer" }}
              onClick={() => onSelectProduct(match)}
            >
              <div className="fw-semibold">{match.product.name}</div>
              <div className="text-muted">
                {match.product.canonicalKey || match.matchedKey}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

// ── Bundle Key Picker ────────────────────────────────────

const BundleKeyPicker: React.FC<{
  value: string;
  onChange: (key: string) => void;
  domain: string;
}> = ({ value, onChange, domain }) => {
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <div className="d-flex gap-1 align-items-center" style={{ flex: 3 }}>
        <Form.Control
          size="sm"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="canonicalKey"
        />
        <Button
          size="sm"
          variant="outline-secondary"
          title="Search for canonical key"
          onClick={() => setOpen(true)}
        >
          <Search size={12} />
        </Button>
      </div>
    );
  }

  return (
    <div style={{ flex: 3 }}>
      <TargetSearchPanel
        domain={domain}
        defaultType="category"
        className="border rounded p-2 bg-white"
        maxHeight={160}
        currentValue={value || undefined}
        onSelectCategory={(cat) => {
          onChange(`category:${domain}:${cat.category}`);
          setOpen(false);
        }}
        onSelectManufacturer={(mfr) => {
          onChange(`mfr:${domain}:${mfr.manufacturerCanonical}`);
          setOpen(false);
        }}
        onSelectProduct={(match) => {
          onChange(match.product.canonicalKey || match.matchedKey);
          setOpen(false);
        }}
        onClose={() => setOpen(false)}
      />
    </div>
  );
};

// ── Target Fields Picker ─────────────────────────────────

const TargetFieldsPicker: React.FC<{
  domain: string;
  onApply: (fields: Partial<IKindredCouponTargetingUpsert>) => void;
}> = ({ domain, onApply }) => {
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <Button
        size="sm"
        variant="outline-secondary"
        onClick={() => setOpen(true)}
      >
        <Search size={12} className="me-1" />
        Search to replace fields
      </Button>
    );
  }

  return (
    <TargetSearchPanel
      domain={domain}
      defaultType="product"
      className="border rounded p-2 bg-light mb-2"
      maxHeight={200}
      onSelectCategory={(cat) => {
        onApply({
          coverage: KindredCouponCoverage.Category,
          categoryPath: cat.category,
          canonicalKey: null,
          manufacturerName: null,
          manufacturerCanonical: null,
          productName: null,
          productUrl: null,
          productSku: null,
          productEan: null,
          productGtin: null,
          productUpc: null,
          productMpn: null,
          productMerchantProductId: null,
          advertiserId: null,
          directNetwork: null,
        });
        setOpen(false);
      }}
      onSelectManufacturer={(mfr) => {
        onApply({
          coverage: KindredCouponCoverage.Manufacturer,
          manufacturerName: mfr.manufacturer,
          manufacturerCanonical: mfr.manufacturerCanonical,
          canonicalKey: null,
          categoryPath: null,
          productName: null,
          productUrl: null,
          productSku: null,
          productEan: null,
          productGtin: null,
          productUpc: null,
          productMpn: null,
          productMerchantProductId: null,
          advertiserId: null,
          directNetwork: null,
        });
        setOpen(false);
      }}
      onSelectProduct={(match) => {
        onApply({
          coverage: KindredCouponCoverage.Product,
          canonicalKey: match.product.canonicalKey || null,
          categoryPath: match.product.categories?.[0] || null,
          manufacturerName: match.product.manufacturer || null,
          manufacturerCanonical: null,
          productName: match.product.name?.trim() || null,
          productUrl: match.product.productUrl || null,
          productSku: match.product.sku || null,
          productEan: match.product.ean || null,
          productGtin: match.product.gtin || null,
          productUpc: match.product.upc || null,
          productMpn: match.product.mpn || null,
          productMerchantProductId:
            match.product.merchantProductId || match.product.id || null,
          advertiserId: match.product.advertiserId || null,
          directNetwork: match.product.affiliateNetwork || null,
        });
        setOpen(false);
      }}
      onClose={() => setOpen(false)}
    />
  );
};

// ── Targeting Panel ─────────────────────────────────────

type TargetingFilter = "all" | "targeted" | "not_targeted";

const TARGETING_FILTER_OPTIONS: { value: TargetingFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "targeted", label: "Targeted" },
  { value: "not_targeted", label: "Not Targeted" },
];

export const TargetingPanel: React.FC<{
  domain: string | null;
  liveCoupons: CouponWithStatus[];
  enabled: boolean;
  /** When provided the panel immediately selects this coupon and fetches its
   *  targeting detail on mount, skipping the bulk coupon-counts call. */
  initialCouponId?: string;
  /** Hide the built-in coupon list when the host already provides one. */
  hideCouponList?: boolean;
  /** Hide the advertiser/products section when the host renders it elsewhere. */
  hideAdvertiser?: boolean;
  /** Keep targeting visible but prevent changes until the row is claimed. */
  readOnly?: boolean;
}> = ({
  domain,
  liveCoupons,
  enabled,
  initialCouponId,
  hideCouponList = false,
  hideAdvertiser = false,
  readOnly = false,
}) => {
  const [targetingFilter, setTargetingFilter] =
    useState<TargetingFilter>("all");
  const [selectedCouponId, setSelectedCouponId] = useState<string | null>(
    initialCouponId ?? null
  );
  const [coverage, setCoverage] = useState<number>(KindredCouponCoverage.None);
  const [priority, setPriority] = useState<number | undefined>(undefined);
  const [expandedTargets, setExpandedTargets] = useState<
    Record<string, boolean>
  >({});
  const [couponForm, setCouponForm] = useState<{
    code: string;
    summary: string;
  }>({
    code: "",
    summary: "",
  });
  const [metadataForm, setMetadataForm] = useState<IKindredMetadataUpdate>({});
  const [categoryPathInput, setCategoryPathInput] = useState("");
  const [categorySuggestions, setCategorySuggestions] =
    useState<ICategoryEntry[]>();
  const [categorySearchLoading, setCategorySearchLoading] = useState(false);
  const [manufacturerQuery, setManufacturerQuery] = useState("");
  const [manufacturerResults, setManufacturerResults] = useState<
    IManufacturerSearchResult[]
  >([]);
  const [manufacturerSearchLoading, setManufacturerSearchLoading] =
    useState(false);
  const [fingerprintForm, setFingerprintForm] = useState<{
    keywords: string;
    categoryPath: string;
    productUrl: string;
    manufacturer: string;
    sku: string;
    ean: string;
    gtin: string;
    upc: string;
    mpn: string;
    merchantProductId: string;
    minimumConfidence: number;
    limit: number;
  }>({
    keywords: "",
    categoryPath: "",
    productUrl: "",
    manufacturer: "",
    sku: "",
    ean: "",
    gtin: "",
    upc: "",
    mpn: "",
    merchantProductId: "",
    minimumConfidence: 0.8,
    limit: 20,
  });
  const [fingerprintOffset, setFingerprintOffset] = useState(0);

  const [targetCounts, setTargetCounts] = useState<Record<string, number>>({});
  const targetCountsPost = usePost<
    { results: ICouponTargetCountItem[] },
    {
      domain: string;
      coupons: Array<{ id: string; brandId: string; code: string }>;
    }
  >();

  // Stable key so the effect only re-fires when coupon IDs actually change,
  // not just because the parent re-rendered and created a new array reference.
  const liveCouponKey = useMemo(
    () => liveCoupons.map((c) => c.id).join(","),
    [liveCoupons]
  );

  useEffect(() => {
    // When a specific coupon is pre-selected we skip the bulk coupon-counts
    // call entirely — the target count is derived from the detail response.
    if (initialCouponId) return;

    if (!enabled || !domain || !liveCouponKey) {
      setTargetCounts({});
      return;
    }

    (async () => {
      try {
        const response = await targetCountsPost.execute(
          "kindred/targeting/coupon-counts",
          {
            domain,
            coupons: liveCoupons.map((coupon) => ({
              id: coupon.id,
              brandId: coupon.brandId,
              code: coupon.code,
            })),
          }
        );

        const countsMap: Record<string, number> = {};
        for (const item of response.results || []) {
          countsMap[item.id] = item.targetCount;
        }
        setTargetCounts(countsMap);
      } catch {
        // error is captured in targetCountsPost.error and rendered below
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, domain, liveCouponKey]);

  // If the selected coupon is removed from the list, clear the selection.
  // Do NOT auto-select the first coupon — detail data is fetched lazily on click.
  useEffect(() => {
    if (!selectedCouponId) return;
    const stillExists = liveCoupons.some(
      (coupon) => coupon.id === selectedCouponId
    );
    if (!stillExists) {
      setSelectedCouponId(null);
    }
  }, [liveCouponKey, selectedCouponId]);

  // Follow the host's selection when it drives the list.
  useEffect(() => {
    if (initialCouponId) setSelectedCouponId(initialCouponId);
  }, [initialCouponId]);

  const {
    data: targetingDetail,
    loading: targetingDetailLoading,
    execute: fetchTargetingDetail,
  } = useGetLazy<IKindredCouponTargetingResponse>();

  // Track which (couponId, domain) pair we last fetched so StrictMode's
  // double-mount doesn't fire a second request. The ref survives the
  // artificial unmount/remount cycle StrictMode uses in dev.
  const fetchedKeyRef = React.useRef<string | null>(null);

  useEffect(() => {
    if (!selectedCouponId || !domain) return;
    const key = `${selectedCouponId}:${domain}`;
    if (fetchedKeyRef.current === key) return; // already in-flight or done
    fetchedKeyRef.current = key;
    fetchTargetingDetail(
      `kindred/targeting/coupons/${selectedCouponId}?domain=${encodeURIComponent(domain)}`
    ).catch(() => {});
    // fetchTargetingDetail is stable (useCallback inside useGetLazy)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCouponId, domain]);

  /** Re-fetch the currently selected coupon's targeting detail (e.g. after
   *  adding or removing a target). Bypasses the dedup ref intentionally. */
  const refetchTargetingDetail = useCallback(async () => {
    if (!selectedCouponId || !domain) return null;
    return fetchTargetingDetail(
      `kindred/targeting/coupons/${selectedCouponId}?domain=${encodeURIComponent(domain)}`
    );
  }, [selectedCouponId, domain, fetchTargetingDetail]);

  useEffect(() => {
    if (!enabled) {
      setSelectedCouponId(null);
    }
  }, [enabled]);

  useEffect(() => {
    if (!targetingDetail) return;
    // In single-coupon mode derive the target count from the detail response
    // (no coupon-counts call was made).
    if (initialCouponId) {
      setTargetCounts({
        [initialCouponId]: targetingDetail.targets?.length ?? 0,
      });
    }
    setCoverage(targetingDetail.coverage ?? KindredCouponCoverage.None);
    setPriority(targetingDetail.priority ?? undefined);
    setCouponForm({
      code: targetingDetail.code,
      summary: targetingDetail.summary ?? "",
    });
    setMetadataForm({
      promoType: targetingDetail.metadata?.promoType ?? null,
      coverage:
        targetingDetail.metadata?.coverage ?? targetingDetail.coverage ?? null,
      discountType: targetingDetail.metadata?.discountType ?? null,
      discounts: targetingDetail.metadata?.discounts ?? null,
      nonMonetaryValue: targetingDetail.metadata?.nonMonetaryValue ?? null,
      language: targetingDetail.metadata?.language ?? null,
      currency: targetingDetail.metadata?.currency ?? null,
      scope: targetingDetail.metadata?.scope ?? null,
      channel: targetingDetail.metadata?.channel ?? null,
      productCategory: targetingDetail.metadata?.productCategory ?? null,
      rawCategory: targetingDetail.metadata?.rawCategory ?? null,
      isStackable: targetingDetail.metadata?.isStackable ?? null,
      requiresReview: targetingDetail.metadata?.requiresReview ?? null,
      reasoning: targetingDetail.metadata?.reasoning ?? null,
      isReviewed: targetingDetail.metadata?.isReviewed ?? null,
    });
  }, [targetingDetail]);

  const selectedLiveCoupon = useMemo(() => {
    if (!selectedCouponId) return null;
    return liveCoupons.find((coupon) => coupon.id === selectedCouponId) || null;
  }, [liveCoupons, selectedCouponId]);

  useEffect(() => {
    if (!selectedLiveCoupon) return;
    setCouponForm((prev) => ({
      code: prev.code || selectedLiveCoupon.code,
      summary: selectedLiveCoupon.summary || "",
    }));
  }, [selectedLiveCoupon]);

  const filteredCoupons = useMemo(() => {
    const items = liveCoupons.map((coupon) => ({
      id: coupon.id,
      code: coupon.code,
      brandId: coupon.brandId,
      brandName: coupon.brandName,
      summary: coupon.summary,
      validTo: coupon.validTo,
      targetCount: targetCounts[coupon.id] ?? 0,
    }));

    if (targetingFilter === "targeted") {
      return items.filter((coupon) => coupon.targetCount > 0);
    }
    if (targetingFilter === "not_targeted") {
      return items.filter((coupon) => coupon.targetCount === 0);
    }
    return items;
  }, [liveCoupons, targetCounts, targetingFilter]);

  const addTargetPost = usePost<
    IKindredCouponTargetingRecord,
    {
      data: IKindredCouponTargetingUpsert;
    }
  >();
  const deleteTargetPost = usePost<{ success: boolean }>();
  const updateTargetPut = usePut<
    IKindredCouponTargetingRecord,
    { data: IKindredCouponTargetingUpsert }
  >();
  const [editingConditionsId, setEditingConditionsId] = useState<string | null>(
    null
  );
  const [conditionsForm, setConditionsForm] = useState<ITargetingConditions>(
    {}
  );
  const [bundleEntries, setBundleEntries] = useState<IBundleRequirement[]>([]);
  const [editingTargetFieldsId, setEditingTargetFieldsId] = useState<
    string | null
  >(null);
  const [editingTargetForm, setEditingTargetForm] =
    useState<IKindredCouponTargetingUpsert>({
      coverage: KindredCouponCoverage.None,
    });
  const updateCouponPut = usePut<
    IKindredCouponDetail,
    { data: IKindredCouponUpdate; comment: string }
  >();
  const fingerprintSearch = usePost<
    IFingerprintSearchResponse,
    IFingerprintSearchRequest
  >();
  const categorySearch = useGetLazy<INetworkCategories>();
  const manufacturerSearch = useGetLazy<{
    manufacturers: IManufacturerSearchResult[];
  }>();

  const handleCategorySearch = useCallback(
    async (query: string) => {
      if (!domain || query.length < 1) {
        setCategorySuggestions([]);
        return;
      }
      setCategorySearchLoading(true);
      try {
        const result = await categorySearch.execute(
          `affiliate-products/categories?network=Awin&domain=${encodeURIComponent(domain)}`
        );
        // Combine categories from top level and nested in advertisers
        const allCategories: ICategoryEntry[] = result?.categories || [];
        const nestedCategories = result?.advertisers
          ? result.advertisers.flatMap((adv: any) => adv.categories || [])
          : [];
        const combined = [...allCategories, ...nestedCategories];
        // Deduplicate by category name
        const uniqueMap = new Map<string, ICategoryEntry>();
        combined.forEach((cat: ICategoryEntry) => {
          if (!uniqueMap.has(cat.category)) {
            uniqueMap.set(cat.category, cat);
          }
        });
        const filtered = Array.from(uniqueMap.values()).filter(
          (c: ICategoryEntry) =>
            c.category.toLowerCase().includes(query.toLowerCase())
        );
        setCategorySuggestions(filtered.slice(0, 20));
      } catch (err) {
        // API endpoint may not be available; allow manual entry
        setCategorySuggestions([]);
      } finally {
        setCategorySearchLoading(false);
      }
    },
    [domain]
  );

  const handleManufacturerSearch = useCallback(
    async (query: string) => {
      if (!domain || query.length < 1) {
        setManufacturerResults([]);
        return;
      }
      setManufacturerSearchLoading(true);
      try {
        const result = await manufacturerSearch.execute(
          `affiliate-products/manufacturers?domain=${encodeURIComponent(domain)}&q=${encodeURIComponent(query)}&limit=20`
        );
        setManufacturerResults(result?.manufacturers || []);
      } catch (err) {
        // API endpoint may not be available; allow manual entry
        setManufacturerResults([]);
      } finally {
        setManufacturerSearchLoading(false);
      }
    },
    [domain]
  );

  const refreshTargetCounts = async () => {
    if (!domain) return;
    if (!liveCoupons.length) {
      setTargetCounts({});
      return;
    }

    const response = await targetCountsPost.execute(
      "kindred/targeting/coupon-counts",
      {
        domain,
        coupons: liveCoupons.map((coupon) => ({
          id: coupon.id,
          brandId: coupon.brandId,
          code:
            coupon.id === selectedCouponId
              ? couponForm.code.trim() || coupon.code
              : coupon.code,
        })),
      }
    );

    const countsMap: Record<string, number> = {};
    for (const item of response.results || []) {
      countsMap[item.id] = item.targetCount;
    }
    setTargetCounts(countsMap);
  };

  const handleAddCategoryTarget = async () => {
    if (!selectedCouponId || !domain || !categoryPathInput.trim()) return;

    await addTargetPost.execute(
      `kindred/targeting/coupons/${selectedCouponId}/targets`,
      {
        data: {
          coverage: KindredCouponCoverage.Category,
          merchantDomain: domain,
          categoryPath: categoryPathInput.trim(),
        },
      }
    );

    setCategoryPathInput("");
    setCategorySuggestions([]);
    const detail = await refetchTargetingDetail();
    if (detail && selectedCouponId) {
      setTargetCounts((prev) => ({
        ...prev,
        [selectedCouponId]: detail.targets.length,
      }));
    }
  };

  const handleAddManufacturerTarget = async (
    mfr: IManufacturerSearchResult
  ) => {
    if (!selectedCouponId || !domain) return;

    await addTargetPost.execute(
      `kindred/targeting/coupons/${selectedCouponId}/targets`,
      {
        data: {
          coverage: KindredCouponCoverage.Manufacturer,
          merchantDomain: domain,
          manufacturerName: mfr.manufacturer,
          manufacturerCanonical: mfr.manufacturerCanonical,
        },
      }
    );

    const detail = await refetchTargetingDetail();
    if (detail && selectedCouponId) {
      setTargetCounts((prev) => ({
        ...prev,
        [selectedCouponId]: detail.targets.length,
      }));
    }
  };

  const handleAddSiteWideTarget = async () => {
    if (!selectedCouponId || !domain) return;

    await addTargetPost.execute(
      `kindred/targeting/coupons/${selectedCouponId}/targets`,
      {
        data: {
          coverage: KindredCouponCoverage.SiteWide,
          merchantDomain: domain,
        },
      }
    );

    const detail = await refetchTargetingDetail();
    if (detail && selectedCouponId) {
      setTargetCounts((prev) => ({
        ...prev,
        [selectedCouponId]: detail.targets.length,
      }));
    }
  };

  const handleAddProductTarget = async (match: IFingerprintSearchResult) => {
    if (!selectedCouponId || !domain) return;

    await addTargetPost.execute(
      `kindred/targeting/coupons/${selectedCouponId}/targets`,
      { data: buildProductTargetPayload(match, domain) }
    );

    const detail = await refetchTargetingDetail();
    if (detail && selectedCouponId) {
      setTargetCounts((prev) => ({
        ...prev,
        [selectedCouponId]: detail.targets.length,
      }));
    }
  };

  const handleToggleDeactivation = async (
    target: IKindredCouponTargetingRecord
  ) => {
    if (!selectedCouponId) return;
    const isCurrentlyDeactivated = !!target.deactivatedAt;

    await updateTargetPut.execute(
      `kindred/targeting/coupons/${selectedCouponId}/targets/${target.id}`,
      {
        data: {
          coverage: target.coverage,
          merchantDomain: target.merchantDomain,
          advertiserId: target.advertiserId,
          categoryPath: target.categoryPath,
          manufacturerName: target.manufacturerName,
          manufacturerCanonical: target.manufacturerCanonical,
          manufacturerId: target.manufacturerId,
          productMerchantProductId: target.productMerchantProductId,
          productSku: target.productSku,
          productEan: target.productEan,
          productGtin: target.productGtin,
          productUpc: target.productUpc,
          productMpn: target.productMpn,
          productName: target.productName,
          productUrl: target.productUrl,
          canonicalKey: target.canonicalKey,
          conditions: target.conditions,
          deactivatedAt: isCurrentlyDeactivated
            ? null
            : new Date().toISOString(),
          deactivationReason: isCurrentlyDeactivated ? null : "Manual toggle",
          deactivatedBy: isCurrentlyDeactivated ? null : "qa-panel",
        },
      }
    );

    refetchTargetingDetail();
  };

  const handleStartEditConditions = (target: IKindredCouponTargetingRecord) => {
    setEditingConditionsId(target.id);
    setConditionsForm(target.conditions || {});
    setBundleEntries(target.conditions?.requiresBundleWith || []);
  };

  const handleSaveConditions = async (
    target: IKindredCouponTargetingRecord
  ) => {
    if (!selectedCouponId) return;

    const conditionsToSave: ITargetingConditions = {
      ...conditionsForm,
      requiresBundleWith: bundleEntries.length > 0 ? bundleEntries : null,
    };

    await updateTargetPut.execute(
      `kindred/targeting/coupons/${selectedCouponId}/targets/${target.id}`,
      {
        data: {
          coverage: target.coverage,
          merchantDomain: target.merchantDomain,
          advertiserId: target.advertiserId,
          categoryPath: target.categoryPath,
          manufacturerName: target.manufacturerName,
          manufacturerCanonical: target.manufacturerCanonical,
          manufacturerId: target.manufacturerId,
          productMerchantProductId: target.productMerchantProductId,
          productSku: target.productSku,
          productEan: target.productEan,
          productGtin: target.productGtin,
          productUpc: target.productUpc,
          productMpn: target.productMpn,
          productName: target.productName,
          productUrl: target.productUrl,
          canonicalKey: target.canonicalKey,
          conditions: conditionsToSave,
          deactivatedAt: target.deactivatedAt,
          deactivationReason: target.deactivationReason,
          deactivatedBy: target.deactivatedBy,
        },
      }
    );

    setEditingConditionsId(null);
    refetchTargetingDetail();
  };

  const handleStartEditTargetFields = (
    target: IKindredCouponTargetingRecord
  ) => {
    setEditingTargetFieldsId(target.id);
    setEditingTargetForm({
      coverage: target.coverage,
      merchantDomain: target.merchantDomain,
      advertiserId: target.advertiserId,
      categoryPath: target.categoryPath,
      manufacturerName: target.manufacturerName,
      manufacturerCanonical: target.manufacturerCanonical,
      manufacturerId: target.manufacturerId,
      productMerchantProductId: target.productMerchantProductId,
      productSku: target.productSku,
      productEan: target.productEan,
      productGtin: target.productGtin,
      productUpc: target.productUpc,
      productMpn: target.productMpn,
      productName: target.productName,
      productUrl: target.productUrl,
      canonicalKey: target.canonicalKey,
      directNetwork: target.directNetwork,
      conditions: target.conditions,
      deactivatedAt: target.deactivatedAt,
      deactivationReason: target.deactivationReason,
      deactivatedBy: target.deactivatedBy,
    });
  };

  const handleSaveTargetFields = async (
    target: IKindredCouponTargetingRecord
  ) => {
    if (!selectedCouponId) return;

    await updateTargetPut.execute(
      `kindred/targeting/coupons/${selectedCouponId}/targets/${target.id}`,
      { data: editingTargetForm }
    );

    setEditingTargetFieldsId(null);
    refetchTargetingDetail();
  };

  const toggleTargetExpanded = (targetId: string) => {
    setExpandedTargets((prev) => ({
      ...prev,
      [targetId]: !prev[targetId],
    }));
  };

  const handleSaveCouponBasics = async () => {
    if (!selectedCouponId) return;

    await updateCouponPut.execute(`kindred/coupons/${selectedCouponId}`, {
      data: {
        summary: couponForm.summary.trim() || null,
        ...(typeof coverage === "number" ? { coverage } : {}),
        ...(priority !== undefined ? { priority } : {}),
        ...(typeof coverage === "number"
          ? { metadata: { ...metadataForm, coverage } }
          : {}),
      },
      comment: "Updated coupon basic details from QA targeting panel",
    });

    refetchTargetingDetail();
  };

  const handleSaveCouponMetadata = async () => {
    if (!selectedCouponId) return;

    await updateCouponPut.execute(`kindred/coupons/${selectedCouponId}`, {
      data: {
        metadata: metadataForm,
      },
      comment: "Updated metadata from QA targeting panel",
    });

    refetchTargetingDetail();
  };

  const handleFingerprintSearch = async (offset = 0) => {
    if (!domain) return;
    const safeOffset = Math.max(0, Math.floor(offset));
    const safeLimit = Number.isFinite(fingerprintForm.limit)
      ? Math.max(1, Math.min(200, Math.floor(fingerprintForm.limit)))
      : 20;
    const safeMinimumConfidence = Number.isFinite(
      fingerprintForm.minimumConfidence
    )
      ? Math.max(0, Math.min(1, fingerprintForm.minimumConfidence))
      : 0;

    setFingerprintOffset(safeOffset);

    await fingerprintSearch.execute("affiliate-products/search/fingerprint", {
      productName: fingerprintForm.keywords.trim() || undefined,
      categoryPath: fingerprintForm.categoryPath.trim() || undefined,
      productUrl: fingerprintForm.productUrl.trim() || undefined,
      domain,
      manufacturer: fingerprintForm.manufacturer.trim() || undefined,
      sku: fingerprintForm.sku.trim() || undefined,
      ean: fingerprintForm.ean.trim() || undefined,
      gtin: fingerprintForm.gtin.trim() || undefined,
      upc: fingerprintForm.upc.trim() || undefined,
      mpn: fingerprintForm.mpn.trim() || undefined,
      merchantProductId: fingerprintForm.merchantProductId.trim() || undefined,
      minimumConfidence: safeMinimumConfidence,
      offset: safeOffset,
      limit: safeLimit,
    });
  };

  const safeLimit = Number.isFinite(fingerprintForm.limit)
    ? Math.max(1, Math.min(200, Math.floor(fingerprintForm.limit)))
    : 20;
  const fingerprintCurrentOffset = fingerprintOffset;
  const fingerprintTotalCount =
    fingerprintSearch.data?.totalCount ?? fingerprintSearch.data?.count ?? 0;
  const fingerprintResults = fingerprintSearch.data?.results || [];
  const fingerprintStart =
    fingerprintTotalCount > 0 ? fingerprintCurrentOffset + 1 : 0;
  const fingerprintEnd = Math.min(
    fingerprintCurrentOffset + safeLimit,
    fingerprintTotalCount
  );
  const fingerprintHasPrev = fingerprintCurrentOffset > 0;
  const fingerprintHasNext =
    fingerprintCurrentOffset + safeLimit < fingerprintTotalCount;

  if (!domain) {
    return (
      <div className="text-muted small py-2">
        Select a domain to configure coupon targeting.
      </div>
    );
  }

  return (
    <fieldset disabled={readOnly}>
      <div>
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div className="small text-muted">
            Targeting domain: <strong>{domain}</strong>
          </div>
          <Form.Select
            size="sm"
            value={targetingFilter}
            onChange={(e) =>
              setTargetingFilter(e.target.value as TargetingFilter)
            }
            style={{ width: 180 }}
          >
            {TARGETING_FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        </div>

        <div className="d-flex gap-2" style={{ minHeight: 520 }}>
          {!hideCouponList && (
            <Card style={{ width: "34%" }}>
              <Card.Header className="py-2">
                Live Coupons ({filteredCoupons.length})
              </Card.Header>
              <Card.Body
                style={{ padding: 0, maxHeight: 520, overflowY: "auto" }}
              >
                {targetCountsPost.loading ? (
                  <div className="text-center py-3">
                    <Spinner animation="border" size="sm" />
                  </div>
                ) : targetCountsPost.error ? (
                  <Alert variant="danger" className="m-2 py-2 mb-0">
                    {targetCountsPost.error.message}
                  </Alert>
                ) : filteredCoupons.length === 0 ? (
                  <div className="text-muted small p-3">No coupons found.</div>
                ) : (
                  <Table hover size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Brand</th>
                        <th>Targets</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCoupons.map((coupon) => (
                        <tr
                          key={coupon.id}
                          style={{ cursor: "pointer" }}
                          className={
                            selectedCouponId === coupon.id ? "table-active" : ""
                          }
                          onClick={() => setSelectedCouponId(coupon.id)}
                        >
                          <td className="small fw-semibold">{coupon.code}</td>
                          <td
                            className="small text-truncate"
                            style={{ maxWidth: 140 }}
                          >
                            {coupon.brandName}
                          </td>
                          <td>
                            <Badge
                              bg={
                                coupon.targetCount > 0 ? "success" : "secondary"
                              }
                            >
                              {coupon.targetCount}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          )}

          <div style={{ width: hideCouponList ? "100%" : "66%" }}>
            {!hideAdvertiser && (
              <AdvertiserPanel
                domain={domain}
                onAddProductTarget={handleAddProductTarget}
                addTargetDisabled={!selectedCouponId}
              />
            )}
            {!selectedCouponId ? (
              <div className="text-muted small py-2">
                Select a coupon to continue.
              </div>
            ) : targetingDetailLoading ? (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" />
              </div>
            ) : !targetingDetail ? (
              <div className="text-muted small py-2">Coupon not found.</div>
            ) : (
              <Accordion defaultActiveKey={["coupon", "targets"]} alwaysOpen>
                <Accordion.Item eventKey="coupon">
                  <Accordion.Header>
                    Coupon Details · {targetingDetail.code} ·{" "}
                    {targetingDetail.brandName}
                  </Accordion.Header>
                  <Accordion.Body>
                    <div className="row g-2 mb-2">
                      <div className="col-md-6">
                        <FieldLabel
                          className="small mb-1"
                          tip="Top-level summary of what this coupon covers. Set per coupon, not per target row."
                        >
                          Coverage
                        </FieldLabel>
                        <Form.Select
                          size="sm"
                          value={coverage}
                          onChange={(e) => setCoverage(Number(e.target.value))}
                        >
                          <option value={KindredCouponCoverage.None}>
                            None
                          </option>
                          <option value={KindredCouponCoverage.SiteWide}>
                            Site-Wide
                          </option>
                          <option value={KindredCouponCoverage.Category}>
                            Category
                          </option>
                          <option value={KindredCouponCoverage.Product}>
                            Product
                          </option>
                          <option value={KindredCouponCoverage.Manufacturer}>
                            Manufacturer
                          </option>
                          <option
                            value={
                              KindredCouponCoverage.Manufacturer |
                              KindredCouponCoverage.Category
                            }
                          >
                            Manufacturer + Category
                          </option>
                        </Form.Select>
                      </div>
                      <div className="col-md-6">
                        <FieldLabel
                          className="small mb-1"
                          tip="Determines which coupon wins when multiple coupons target the same product/category/manufacturer. Higher priority wins."
                        >
                          Priority
                        </FieldLabel>
                        <Form.Control
                          size="sm"
                          type="number"
                          value={priority ?? ""}
                          onChange={(e) => {
                            const raw = e.target.value;
                            setPriority(raw === "" ? undefined : Number(raw));
                          }}
                        />
                      </div>
                      <div className="col-12">
                        <Form.Label className="small mb-1">
                          Description
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          size="sm"
                          value={couponForm.summary}
                          onChange={(e) =>
                            setCouponForm((prev) => ({
                              ...prev,
                              summary: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div className="col-12">
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={handleSaveCouponBasics}
                          disabled={
                            updateCouponPut.loading || !couponForm.code.trim()
                          }
                        >
                          {updateCouponPut.loading ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            "Save Basic Details"
                          )}
                        </Button>
                      </div>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="metadata">
                  <Accordion.Header>Metadata</Accordion.Header>
                  <Accordion.Body>
                    <MetadataEditor
                      metadata={metadataForm}
                      onChange={(m) =>
                        setMetadataForm(m as IKindredMetadataUpdate)
                      }
                    />
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={handleSaveCouponMetadata}
                      disabled={updateCouponPut.loading}
                    >
                      {updateCouponPut.loading ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        "Save Metadata + Coverage"
                      )}
                    </Button>
                  </Accordion.Body>
                </Accordion.Item>

                {(targetingDetail.coverage & KindredCouponCoverage.SiteWide) ===
                  KindredCouponCoverage.SiteWide && (
                  <Accordion.Item eventKey="sitewide-target">
                    <Accordion.Header>Add Site-Wide Target</Accordion.Header>
                    <Accordion.Body>
                      <p className="small text-muted mb-2">
                        This coupon is site-wide, so a target row needs no
                        category, product, or manufacturer anchor. Add a
                        site-wide target to attach conditions (e.g. minimum
                        spend, minimum quantity) or bundle requirements that
                        gate the whole basket. After adding, edit its conditions
                        from the Existing Targets list below.
                      </p>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={handleAddSiteWideTarget}
                        disabled={addTargetPost.loading}
                      >
                        {addTargetPost.loading ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <>
                            <Plus size={14} className="me-1" />
                            Add Site-Wide Target
                          </>
                        )}
                      </Button>
                    </Accordion.Body>
                  </Accordion.Item>
                )}

                <Accordion.Item eventKey="category-target">
                  <Accordion.Header>Add Category Target</Accordion.Header>
                  <Accordion.Body>
                    <InputGroup size="sm" className="mb-2">
                      <Form.Control
                        placeholder="Search or type category path..."
                        value={categoryPathInput}
                        onChange={(e) => setCategoryPathInput(e.target.value)}
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={() => handleCategorySearch(categoryPathInput)}
                        disabled={
                          !categoryPathInput.trim() || categorySearchLoading
                        }
                      >
                        <Search size={14} />
                      </Button>
                      <Button
                        variant="outline-primary"
                        onClick={handleAddCategoryTarget}
                        disabled={
                          !categoryPathInput.trim() || addTargetPost.loading
                        }
                      >
                        <Plus size={14} className="me-1" />
                        Add
                      </Button>
                    </InputGroup>
                    {categorySearchLoading && (
                      <div className="mt-1">
                        <Spinner animation="border" size="sm" />
                      </div>
                    )}
                    {categorySuggestions?.length != null &&
                      categorySuggestions.length > 0 && (
                        <div
                          className="border rounded mt-1"
                          style={{ maxHeight: 160, overflowY: "auto" }}
                        >
                          {categorySuggestions?.map((cat) => (
                            <div
                              key={cat.category}
                              className="px-2 py-1 small border-bottom d-flex justify-content-between align-items-center"
                              style={{ cursor: "pointer" }}
                              onClick={() => {
                                setCategoryPathInput(cat.category);
                                setCategorySuggestions([]);
                              }}
                            >
                              <span>{cat.category}</span>
                              <Badge bg="secondary" className="ms-2">
                                {cat.productCount}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      )}
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="manufacturer-target">
                  <Accordion.Header>Add Manufacturer Target</Accordion.Header>
                  <Accordion.Body>
                    <InputGroup size="sm" className="mb-2">
                      <Form.Control
                        placeholder="Search manufacturer (e.g. Logitech)..."
                        value={manufacturerQuery}
                        onChange={(e) => setManufacturerQuery(e.target.value)}
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={() =>
                          handleManufacturerSearch(manufacturerQuery)
                        }
                        disabled={
                          !manufacturerQuery.trim() || manufacturerSearchLoading
                        }
                      >
                        <Search size={14} />
                      </Button>
                    </InputGroup>
                    {manufacturerSearchLoading && (
                      <div className="mt-1">
                        <Spinner animation="border" size="sm" />
                      </div>
                    )}
                    {manufacturerResults.length > 0 && (
                      <Table hover size="sm" className="mt-1 mb-0">
                        <thead>
                          <tr>
                            <th>Manufacturer</th>
                            <th>Products</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {manufacturerResults.map((mfr) => (
                            <tr key={mfr.manufacturerCanonical}>
                              <td className="small">
                                <strong>{mfr.manufacturer}</strong>
                                <span className="text-muted ms-1">
                                  ({mfr.manufacturerCanonical})
                                </span>
                              </td>
                              <td>
                                <Badge bg="secondary">{mfr.productCount}</Badge>
                              </td>
                              <td>
                                <Button
                                  size="sm"
                                  variant="outline-success"
                                  onClick={() =>
                                    handleAddManufacturerTarget(mfr)
                                  }
                                  disabled={addTargetPost.loading}
                                >
                                  <Plus size={12} className="me-1" />
                                  Add
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="fingerprint">
                  <Accordion.Header>Fingerprint Search</Accordion.Header>
                  <Accordion.Body>
                    <div className="row g-2">
                      <div className="col-md-4">
                        <Form.Control
                          size="sm"
                          placeholder="Keywords / product name"
                          value={fingerprintForm.keywords}
                          onChange={(e) =>
                            setFingerprintForm((prev) => ({
                              ...prev,
                              keywords: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div className="col-md-4">
                        <Form.Control
                          size="sm"
                          placeholder="Category path"
                          value={fingerprintForm.categoryPath}
                          onChange={(e) =>
                            setFingerprintForm((prev) => ({
                              ...prev,
                              categoryPath: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div className="col-md-4">
                        <Form.Control
                          size="sm"
                          placeholder="Manufacturer (e.g. logitech)"
                          value={fingerprintForm.manufacturer}
                          onChange={(e) =>
                            setFingerprintForm((prev) => ({
                              ...prev,
                              manufacturer: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div className="col-md-6">
                        <Form.Control
                          size="sm"
                          placeholder="Product URL"
                          value={fingerprintForm.productUrl}
                          onChange={(e) =>
                            setFingerprintForm((prev) => ({
                              ...prev,
                              productUrl: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div className="col-md-2">
                        <Form.Control
                          size="sm"
                          type="number"
                          min={0}
                          max={1}
                          step={0.01}
                          value={fingerprintForm.minimumConfidence}
                          onChange={(e) =>
                            setFingerprintForm((prev) => ({
                              ...prev,
                              minimumConfidence: Number(e.target.value),
                            }))
                          }
                        />
                      </div>
                      <div className="col-md-1">
                        <Form.Control
                          size="sm"
                          type="number"
                          min={1}
                          max={200}
                          value={fingerprintForm.limit}
                          onChange={(e) =>
                            setFingerprintForm((prev) => ({
                              ...prev,
                              limit: Number(e.target.value),
                            }))
                          }
                        />
                      </div>
                      <div className="col-md-1">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleFingerprintSearch(0)}
                          disabled={fingerprintSearch.loading}
                          className="w-100"
                        >
                          <Search size={14} className="me-1" />
                          Search
                        </Button>
                      </div>
                    </div>

                    {fingerprintSearch.error && (
                      <Alert variant="danger" className="mt-2 py-2 mb-0">
                        {fingerprintSearch.error.message}
                      </Alert>
                    )}

                    {fingerprintSearch.data && (
                      <>
                        <div className="d-flex align-items-center justify-content-between mt-2 small text-muted">
                          <div>
                            Showing {fingerprintResults.length} result
                            {fingerprintResults.length === 1 ? "" : "s"}
                            {fingerprintTotalCount > 0
                              ? ` (${fingerprintStart}-${fingerprintEnd} of ${fingerprintTotalCount})`
                              : ""}
                          </div>
                          <div className="d-flex gap-1">
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() =>
                                handleFingerprintSearch(
                                  fingerprintCurrentOffset - safeLimit
                                )
                              }
                              disabled={
                                fingerprintSearch.loading || !fingerprintHasPrev
                              }
                            >
                              Prev
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() =>
                                handleFingerprintSearch(
                                  fingerprintCurrentOffset + safeLimit
                                )
                              }
                              disabled={
                                fingerprintSearch.loading || !fingerprintHasNext
                              }
                            >
                              Next
                            </Button>
                          </div>
                        </div>

                        <Table hover striped size="sm" className="mt-2 mb-0">
                          <thead>
                            <tr>
                              <th>Product</th>
                              <th>Advertiser</th>
                              <th>Manufacturer</th>
                              <th>Category</th>
                              <th>Confidence</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {fingerprintResults.map((match) => (
                              <tr
                                key={`${match.product.id}-${match.matchedKey}`}
                              >
                                <td className="small">
                                  <div className="fw-semibold">
                                    {match.product.name}
                                  </div>
                                  {match.product.productUrl && (
                                    <a
                                      href={match.product.productUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-decoration-none"
                                    >
                                      <ExternalLink
                                        size={12}
                                        className="me-1"
                                      />
                                      View
                                    </a>
                                  )}
                                </td>
                                <td className="small">
                                  <div
                                    className="text-truncate"
                                    title={match.product.advertiserName}
                                  >
                                    {match.product.advertiserName || "-"}
                                  </div>
                                  {match.product.advertiserId && (
                                    <small
                                      className="text-muted d-block text-truncate"
                                      title={match.product.advertiserId}
                                    >
                                      ID: {match.product.advertiserId}
                                    </small>
                                  )}
                                </td>
                                <td className="small">
                                  {match.product.manufacturer || "-"}
                                </td>
                                <td className="small">
                                  {match.product.categories?.[0] || "-"}
                                </td>
                                <td>
                                  <Badge
                                    bg={
                                      match.matchConfidence >= 0.7
                                        ? "success"
                                        : "warning"
                                    }
                                  >
                                    {match.matchConfidence.toFixed(2)}
                                  </Badge>
                                </td>
                                <td>
                                  <Button
                                    size="sm"
                                    variant="outline-success"
                                    onClick={() =>
                                      handleAddProductTarget(match)
                                    }
                                    disabled={addTargetPost.loading}
                                  >
                                    <Plus size={12} className="me-1" />
                                    Add Target
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </>
                    )}
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="targets">
                  <Accordion.Header>
                    Existing Targets ({targetingDetail.targets.length})
                  </Accordion.Header>
                  <Accordion.Body style={{ maxHeight: 400, overflowY: "auto" }}>
                    {targetingDetail.targets.length === 0 ? (
                      <div className="text-muted small">No targets yet.</div>
                    ) : (
                      <Table hover size="sm" className="mb-0">
                        <thead>
                          <tr>
                            <th>Active</th>
                            <th>Coverage</th>
                            <th>Category</th>
                            <th>Manufacturer</th>
                            <th>Product</th>
                            <th>Conditions</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {targetingDetail.targets.map((target) => {
                            const isDeactivated = !!target.deactivatedAt;
                            const coverageLabel = (() => {
                              const c = target.coverage;
                              const parts: string[] = [];
                              if (c & KindredCouponCoverage.SiteWide)
                                parts.push("Site-Wide");
                              if (c & KindredCouponCoverage.Category)
                                parts.push("Category");
                              if (c & KindredCouponCoverage.Product)
                                parts.push("Product");
                              if (c & KindredCouponCoverage.Manufacturer)
                                parts.push("Manufacturer");
                              return parts.length ? parts.join(" + ") : "None";
                            })();
                            const hasConditions = !!(
                              target.conditions &&
                              (target.conditions.minimumSpend != null ||
                                target.conditions.minimumLinePrice != null ||
                                target.conditions.minimumQuantity != null ||
                                target.conditions.maxQuantity != null ||
                                target.conditions.application != null ||
                                target.conditions.discountAppliesToCount !=
                                  null ||
                                (target.conditions.requiresBundleWith &&
                                  target.conditions.requiresBundleWith.length >
                                    0))
                            );

                            return (
                              <React.Fragment key={target.id}>
                                <tr
                                  className={
                                    isDeactivated ? "table-secondary" : ""
                                  }
                                >
                                  <td>
                                    <Button
                                      size="sm"
                                      variant="link"
                                      className="p-0"
                                      title={
                                        isDeactivated
                                          ? "Activate target"
                                          : "Deactivate target"
                                      }
                                      onClick={() =>
                                        handleToggleDeactivation(target)
                                      }
                                      disabled={updateTargetPut.loading}
                                    >
                                      {isDeactivated ? (
                                        <ToggleLeft
                                          size={18}
                                          className="text-secondary"
                                        />
                                      ) : (
                                        <ToggleRight
                                          size={18}
                                          className="text-success"
                                        />
                                      )}
                                    </Button>
                                  </td>
                                  <td className="small">{coverageLabel}</td>
                                  <td
                                    className="small text-truncate"
                                    style={{ maxWidth: 150 }}
                                  >
                                    {target.categoryPath || "-"}
                                  </td>
                                  <td
                                    className="small text-truncate"
                                    style={{ maxWidth: 120 }}
                                  >
                                    {target.manufacturerName || "-"}
                                  </td>
                                  <td
                                    className="small text-truncate"
                                    style={{ maxWidth: 180 }}
                                  >
                                    {target.productName ||
                                      target.productMerchantProductId ||
                                      "-"}
                                  </td>
                                  <td>
                                    {hasConditions ? (
                                      <Badge bg="info">Yes</Badge>
                                    ) : (
                                      <span className="text-muted small">
                                        -
                                      </span>
                                    )}
                                  </td>
                                  <td className="d-flex gap-1">
                                    <Button
                                      size="sm"
                                      variant="outline-secondary"
                                      onClick={() =>
                                        toggleTargetExpanded(target.id)
                                      }
                                    >
                                      {expandedTargets[target.id]
                                        ? "Hide"
                                        : "Show"}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline-warning"
                                      title="Edit target fields"
                                      onClick={() =>
                                        handleStartEditTargetFields(target)
                                      }
                                    >
                                      <Edit2 size={12} />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline-primary"
                                      title="Edit conditions"
                                      onClick={() =>
                                        handleStartEditConditions(target)
                                      }
                                    >
                                      <Edit2 size={12} />
                                    </Button>
                                  </td>
                                </tr>
                                {expandedTargets[target.id] && (
                                  <tr>
                                    <td colSpan={7} className="bg-light">
                                      <div className="row g-2 small">
                                        <div className="col-md-4">
                                          <strong>Target ID:</strong>{" "}
                                          {target.id}
                                        </div>
                                        <div className="col-md-4">
                                          <strong>Coupon Code:</strong>{" "}
                                          {target.code}
                                        </div>
                                        <div className="col-md-4">
                                          <strong>Brand ID:</strong>{" "}
                                          {target.brandId}
                                        </div>
                                        <div className="col-md-4">
                                          <strong>Merchant Domain:</strong>{" "}
                                          {target.merchantDomain || "-"}
                                        </div>
                                        <div className="col-md-4">
                                          <strong>Advertiser ID:</strong>{" "}
                                          {target.advertiserId || "-"}
                                        </div>
                                        <div className="col-md-4">
                                          <strong>Canonical Key:</strong>{" "}
                                          {target.canonicalKey || "-"}
                                        </div>
                                        <div className="col-md-4">
                                          <strong>Manufacturer:</strong>{" "}
                                          {target.manufacturerName || "-"}
                                        </div>
                                        <div className="col-md-4">
                                          <strong>
                                            Manufacturer Canonical:
                                          </strong>{" "}
                                          {target.manufacturerCanonical || "-"}
                                        </div>
                                        <div className="col-md-4">
                                          <strong>Product Name:</strong>{" "}
                                          {target.productName || "-"}
                                        </div>
                                        <div className="col-md-4">
                                          <strong>Merchant Product ID:</strong>{" "}
                                          {target.productMerchantProductId ||
                                            "-"}
                                        </div>
                                        <div className="col-md-4">
                                          <strong>Product URL:</strong>{" "}
                                          {target.productUrl ? (
                                            <a
                                              href={target.productUrl}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                            >
                                              Open
                                            </a>
                                          ) : (
                                            "-"
                                          )}
                                        </div>
                                        <div className="col-md-4">
                                          <strong>Category Path:</strong>{" "}
                                          {target.categoryPath || "-"}
                                        </div>
                                        <div className="col-md-4">
                                          <strong>SKU:</strong>{" "}
                                          {target.productSku || "-"}
                                        </div>
                                        <div className="col-md-4">
                                          <strong>EAN:</strong>{" "}
                                          {target.productEan || "-"}
                                        </div>
                                        <div className="col-md-4">
                                          <strong>MPN:</strong>{" "}
                                          {target.productMpn || "-"}
                                        </div>
                                        <div className="col-md-4">
                                          <strong>Direct Network:</strong>{" "}
                                          {target.directNetwork || "-"}
                                        </div>
                                        {isDeactivated && (
                                          <>
                                            <div className="col-md-4">
                                              <strong>Deactivated At:</strong>{" "}
                                              {target.deactivatedAt}
                                            </div>
                                            <div className="col-md-4">
                                              <strong>Reason:</strong>{" "}
                                              {target.deactivationReason || "-"}
                                            </div>
                                            <div className="col-md-4">
                                              <strong>Deactivated By:</strong>{" "}
                                              {target.deactivatedBy || "-"}
                                            </div>
                                          </>
                                        )}
                                        {target.conditions && (
                                          <div className="col-12 mt-1">
                                            <strong>Conditions:</strong>
                                            <div className="ms-2">
                                              {target.conditions.minimumSpend !=
                                                null && (
                                                <span className="me-3">
                                                  Min Spend: £
                                                  {
                                                    target.conditions
                                                      .minimumSpend
                                                  }
                                                </span>
                                              )}
                                              {target.conditions
                                                .minimumLinePrice != null && (
                                                <span className="me-3">
                                                  Min Line Price: £
                                                  {
                                                    target.conditions
                                                      .minimumLinePrice
                                                  }
                                                </span>
                                              )}
                                              {target.conditions
                                                .minimumQuantity != null && (
                                                <span className="me-3">
                                                  Min Qty:{" "}
                                                  {
                                                    target.conditions
                                                      .minimumQuantity
                                                  }
                                                </span>
                                              )}
                                              {target.conditions.maxQuantity !=
                                                null && (
                                                <span className="me-3">
                                                  Max Qty:{" "}
                                                  {
                                                    target.conditions
                                                      .maxQuantity
                                                  }
                                                </span>
                                              )}
                                              {target.conditions.application !=
                                                null && (
                                                <span className="me-3">
                                                  Application:{" "}
                                                  {target.conditions
                                                    .application ===
                                                  DiscountApplication.All
                                                    ? "All"
                                                    : target.conditions
                                                          .application ===
                                                        DiscountApplication.Cheapest
                                                      ? "Cheapest"
                                                      : "Most Expensive"}
                                                </span>
                                              )}
                                              {target.conditions
                                                .discountAppliesToCount !=
                                                null && (
                                                <span className="me-3">
                                                  Applies To:{" "}
                                                  {
                                                    target.conditions
                                                      .discountAppliesToCount
                                                  }
                                                </span>
                                              )}
                                              {target.conditions
                                                .requiresBundleWith &&
                                                target.conditions
                                                  .requiresBundleWith.length >
                                                  0 && (
                                                  <div>
                                                    Bundle:{" "}
                                                    {target.conditions.requiresBundleWith
                                                      .map(
                                                        (b) => b.canonicalKey
                                                      )
                                                      .join(", ")}
                                                  </div>
                                                )}
                                            </div>
                                          </div>
                                        )}
                                        <div className="col-md-4">
                                          <strong>Created At:</strong>{" "}
                                          {target.createdAt}
                                        </div>
                                        <div className="col-md-4">
                                          <strong>Updated At:</strong>{" "}
                                          {target.updatedAt || "-"}
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                                {editingTargetFieldsId === target.id && (
                                  <tr>
                                    <td
                                      colSpan={7}
                                      className="bg-white border-top"
                                    >
                                      <div className="p-2">
                                        <strong className="small">
                                          Edit Target Fields
                                        </strong>
                                        <div className="mt-2">
                                          <TargetFieldsPicker
                                            domain={domain}
                                            onApply={(fields) =>
                                              setEditingTargetForm((prev) => ({
                                                ...prev,
                                                ...fields,
                                              }))
                                            }
                                          />
                                        </div>
                                        <div className="row g-2 mt-1">
                                          <div className="col-md-4">
                                            <Form.Label className="small mb-1">
                                              Coverage
                                            </Form.Label>
                                            <Form.Select
                                              size="sm"
                                              value={
                                                editingTargetForm.coverage ??
                                                KindredCouponCoverage.None
                                              }
                                              onChange={(e) =>
                                                setEditingTargetForm(
                                                  (prev) => ({
                                                    ...prev,
                                                    coverage: Number(
                                                      e.target.value
                                                    ),
                                                  })
                                                )
                                              }
                                            >
                                              <option
                                                value={
                                                  KindredCouponCoverage.None
                                                }
                                              >
                                                None
                                              </option>
                                              <option
                                                value={
                                                  KindredCouponCoverage.SiteWide
                                                }
                                              >
                                                Site-Wide
                                              </option>
                                              <option
                                                value={
                                                  KindredCouponCoverage.Category
                                                }
                                              >
                                                Category
                                              </option>
                                              <option
                                                value={
                                                  KindredCouponCoverage.Product
                                                }
                                              >
                                                Product
                                              </option>
                                              <option
                                                value={
                                                  KindredCouponCoverage.Manufacturer
                                                }
                                              >
                                                Manufacturer
                                              </option>
                                              <option
                                                value={
                                                  KindredCouponCoverage.Manufacturer |
                                                  KindredCouponCoverage.Category
                                                }
                                              >
                                                Manufacturer + Category
                                              </option>
                                            </Form.Select>
                                          </div>
                                          <div className="col-md-8">
                                            <Form.Label className="small mb-1">
                                              Canonical Key
                                            </Form.Label>
                                            <Form.Control
                                              size="sm"
                                              value={
                                                editingTargetForm.canonicalKey ??
                                                ""
                                              }
                                              onChange={(e) =>
                                                setEditingTargetForm(
                                                  (prev) => ({
                                                    ...prev,
                                                    canonicalKey:
                                                      e.target.value || null,
                                                  })
                                                )
                                              }
                                            />
                                          </div>
                                          <div className="col-md-6">
                                            <Form.Label className="small mb-1">
                                              Category Path
                                            </Form.Label>
                                            <Form.Control
                                              size="sm"
                                              value={
                                                editingTargetForm.categoryPath ??
                                                ""
                                              }
                                              onChange={(e) =>
                                                setEditingTargetForm(
                                                  (prev) => ({
                                                    ...prev,
                                                    categoryPath:
                                                      e.target.value || null,
                                                  })
                                                )
                                              }
                                            />
                                          </div>
                                          <div className="col-md-3">
                                            <Form.Label className="small mb-1">
                                              Manufacturer Name
                                            </Form.Label>
                                            <Form.Control
                                              size="sm"
                                              value={
                                                editingTargetForm.manufacturerName ??
                                                ""
                                              }
                                              onChange={(e) =>
                                                setEditingTargetForm(
                                                  (prev) => ({
                                                    ...prev,
                                                    manufacturerName:
                                                      e.target.value || null,
                                                  })
                                                )
                                              }
                                            />
                                          </div>
                                          <div className="col-md-3">
                                            <Form.Label className="small mb-1">
                                              Manufacturer Canonical
                                            </Form.Label>
                                            <Form.Control
                                              size="sm"
                                              value={
                                                editingTargetForm.manufacturerCanonical ??
                                                ""
                                              }
                                              onChange={(e) =>
                                                setEditingTargetForm(
                                                  (prev) => ({
                                                    ...prev,
                                                    manufacturerCanonical:
                                                      e.target.value || null,
                                                  })
                                                )
                                              }
                                            />
                                          </div>
                                          <div className="col-md-6">
                                            <Form.Label className="small mb-1">
                                              Product Name
                                            </Form.Label>
                                            <Form.Control
                                              size="sm"
                                              value={
                                                editingTargetForm.productName ??
                                                ""
                                              }
                                              onChange={(e) =>
                                                setEditingTargetForm(
                                                  (prev) => ({
                                                    ...prev,
                                                    productName:
                                                      e.target.value || null,
                                                  })
                                                )
                                              }
                                            />
                                          </div>
                                          <div className="col-md-6">
                                            <Form.Label className="small mb-1">
                                              Product URL
                                            </Form.Label>
                                            <Form.Control
                                              size="sm"
                                              value={
                                                editingTargetForm.productUrl ??
                                                ""
                                              }
                                              onChange={(e) =>
                                                setEditingTargetForm(
                                                  (prev) => ({
                                                    ...prev,
                                                    productUrl:
                                                      e.target.value || null,
                                                  })
                                                )
                                              }
                                            />
                                          </div>
                                          <div className="col-md-3">
                                            <Form.Label className="small mb-1">
                                              Advertiser ID
                                            </Form.Label>
                                            <Form.Control
                                              size="sm"
                                              value={
                                                editingTargetForm.advertiserId ??
                                                ""
                                              }
                                              onChange={(e) =>
                                                setEditingTargetForm(
                                                  (prev) => ({
                                                    ...prev,
                                                    advertiserId:
                                                      e.target.value || null,
                                                  })
                                                )
                                              }
                                            />
                                          </div>
                                          <div className="col-md-3">
                                            <Form.Label className="small mb-1">
                                              SKU
                                            </Form.Label>
                                            <Form.Control
                                              size="sm"
                                              value={
                                                editingTargetForm.productSku ??
                                                ""
                                              }
                                              onChange={(e) =>
                                                setEditingTargetForm(
                                                  (prev) => ({
                                                    ...prev,
                                                    productSku:
                                                      e.target.value || null,
                                                  })
                                                )
                                              }
                                            />
                                          </div>
                                          <div className="col-md-3">
                                            <Form.Label className="small mb-1">
                                              EAN
                                            </Form.Label>
                                            <Form.Control
                                              size="sm"
                                              value={
                                                editingTargetForm.productEan ??
                                                ""
                                              }
                                              onChange={(e) =>
                                                setEditingTargetForm(
                                                  (prev) => ({
                                                    ...prev,
                                                    productEan:
                                                      e.target.value || null,
                                                  })
                                                )
                                              }
                                            />
                                          </div>
                                          <div className="col-md-3">
                                            <Form.Label className="small mb-1">
                                              MPN
                                            </Form.Label>
                                            <Form.Control
                                              size="sm"
                                              value={
                                                editingTargetForm.productMpn ??
                                                ""
                                              }
                                              onChange={(e) =>
                                                setEditingTargetForm(
                                                  (prev) => ({
                                                    ...prev,
                                                    productMpn:
                                                      e.target.value || null,
                                                  })
                                                )
                                              }
                                            />
                                          </div>
                                          <div className="col-md-4">
                                            <Form.Label className="small mb-1">
                                              Direct Network
                                            </Form.Label>
                                            <Form.Control
                                              size="sm"
                                              value={
                                                editingTargetForm.directNetwork ??
                                                ""
                                              }
                                              onChange={(e) =>
                                                setEditingTargetForm(
                                                  (prev) => ({
                                                    ...prev,
                                                    directNetwork:
                                                      e.target.value || null,
                                                  })
                                                )
                                              }
                                            />
                                          </div>
                                          <div className="col-12 d-flex gap-1 mt-2">
                                            <Button
                                              size="sm"
                                              variant="primary"
                                              onClick={() =>
                                                handleSaveTargetFields(target)
                                              }
                                              disabled={updateTargetPut.loading}
                                            >
                                              <Save
                                                size={12}
                                                className="me-1"
                                              />
                                              Save
                                            </Button>
                                            <Button
                                              size="sm"
                                              variant="outline-secondary"
                                              onClick={() =>
                                                setEditingTargetFieldsId(null)
                                              }
                                            >
                                              Cancel
                                            </Button>
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                                {editingConditionsId === target.id && (
                                  <tr>
                                    <td
                                      colSpan={7}
                                      className="bg-white border-top"
                                    >
                                      <div className="p-2">
                                        <strong className="small">
                                          Edit Conditions
                                        </strong>
                                        <div className="row g-2 mt-1">
                                          <div className="col-md-3">
                                            <FieldLabel tip="Minimum spend within this target's scope before the discount fires. For a basket-wide threshold use metadata › discounts › minimumSpend instead.">
                                              Min Spend
                                            </FieldLabel>
                                            <Form.Control
                                              size="sm"
                                              type="number"
                                              min={0}
                                              step={0.01}
                                              placeholder="e.g. 100"
                                              value={
                                                conditionsForm.minimumSpend ??
                                                ""
                                              }
                                              onChange={(e) =>
                                                setConditionsForm((prev) => ({
                                                  ...prev,
                                                  minimumSpend: e.target.value
                                                    ? Number(e.target.value)
                                                    : null,
                                                }))
                                              }
                                            />
                                          </div>
                                          <div className="col-md-3">
                                            <FieldLabel tip="Minimum price of a single qualifying line item. The discount only applies to items at or above this price (e.g. 399 for Large Kitchen Appliances over 399).">
                                              Min Line Price
                                            </FieldLabel>
                                            <Form.Control
                                              size="sm"
                                              type="number"
                                              min={0}
                                              step={0.01}
                                              placeholder="e.g. 399"
                                              value={
                                                conditionsForm.minimumLinePrice ??
                                                ""
                                              }
                                              onChange={(e) =>
                                                setConditionsForm((prev) => ({
                                                  ...prev,
                                                  minimumLinePrice: e.target
                                                    .value
                                                    ? Number(e.target.value)
                                                    : null,
                                                }))
                                              }
                                            />
                                          </div>
                                          <div className="col-md-3">
                                            <FieldLabel tip="Minimum number of qualifying items in the basket. Use for buy-N-get-discount offers (e.g. 2 for '10% off cheapest when buying 2 or more').">
                                              Min Quantity
                                            </FieldLabel>
                                            <Form.Control
                                              size="sm"
                                              type="number"
                                              min={1}
                                              step={1}
                                              placeholder="e.g. 2"
                                              value={
                                                conditionsForm.minimumQuantity ??
                                                ""
                                              }
                                              onChange={(e) =>
                                                setConditionsForm((prev) => ({
                                                  ...prev,
                                                  minimumQuantity: e.target
                                                    .value
                                                    ? Number(e.target.value)
                                                    : null,
                                                }))
                                              }
                                            />
                                          </div>
                                          <div className="col-md-3">
                                            <FieldLabel tip="Maximum number of qualifying items the discount can apply to. Leave empty for no cap.">
                                              Max Quantity
                                            </FieldLabel>
                                            <Form.Control
                                              size="sm"
                                              type="number"
                                              min={1}
                                              step={1}
                                              placeholder="e.g. 5"
                                              value={
                                                conditionsForm.maxQuantity ?? ""
                                              }
                                              onChange={(e) =>
                                                setConditionsForm((prev) => ({
                                                  ...prev,
                                                  maxQuantity: e.target.value
                                                    ? Number(e.target.value)
                                                    : null,
                                                }))
                                              }
                                            />
                                          </div>
                                          <div className="col-md-3">
                                            <FieldLabel tip="Which items get the discount. All = every qualifying item. Cheapest = lowest-priced item(s). Most Expensive = highest-priced item(s). Use Cheapest for 'buy N get Nth item off' offers.">
                                              Application
                                            </FieldLabel>
                                            <Form.Select
                                              size="sm"
                                              value={
                                                conditionsForm.application ?? ""
                                              }
                                              onChange={(e) =>
                                                setConditionsForm((prev) => ({
                                                  ...prev,
                                                  application:
                                                    e.target.value !== ""
                                                      ? Number(e.target.value)
                                                      : null,
                                                }))
                                              }
                                            >
                                              <option value="">None</option>
                                              <option
                                                value={DiscountApplication.All}
                                              >
                                                All
                                              </option>
                                              <option
                                                value={
                                                  DiscountApplication.Cheapest
                                                }
                                              >
                                                Cheapest
                                              </option>
                                              <option
                                                value={
                                                  DiscountApplication.MostExpensive
                                                }
                                              >
                                                Most Expensive
                                              </option>
                                            </Form.Select>
                                          </div>
                                          <div className="col-md-3">
                                            <FieldLabel tip="How many items the Application logic acts on. Typically 1 — e.g. discount applies to the 1 cheapest item. Combine with Min Quantity >= 2 and Application = Cheapest for buy-N-get-1-off deals.">
                                              Applies To Count
                                            </FieldLabel>
                                            <Form.Control
                                              size="sm"
                                              type="number"
                                              min={1}
                                              step={1}
                                              placeholder="e.g. 1"
                                              value={
                                                conditionsForm.discountAppliesToCount ??
                                                ""
                                              }
                                              onChange={(e) =>
                                                setConditionsForm((prev) => ({
                                                  ...prev,
                                                  discountAppliesToCount: e
                                                    .target.value
                                                    ? Number(e.target.value)
                                                    : null,
                                                }))
                                              }
                                            />
                                          </div>
                                          <div className="col-md-3">
                                            <FieldLabel tip="Override switch. Set to Inactive to temporarily disable these conditions without deleting them.">
                                              Conditions Active
                                            </FieldLabel>
                                            <Form.Select
                                              size="sm"
                                              value={
                                                conditionsForm.isActive ===
                                                false
                                                  ? "false"
                                                  : "true"
                                              }
                                              onChange={(e) =>
                                                setConditionsForm((prev) => ({
                                                  ...prev,
                                                  isActive:
                                                    e.target.value === "true",
                                                }))
                                              }
                                            >
                                              <option value="true">
                                                Active
                                              </option>
                                              <option value="false">
                                                Inactive
                                              </option>
                                            </Form.Select>
                                          </div>
                                          <div className="col-12 mt-2">
                                            <FieldLabel
                                              className="small mb-1 fw-semibold"
                                              tip="The basket must also contain items matching each entry's canonical key. Use the search picker (🔍) to find a category, product, or manufacturer — the key is built automatically."
                                            >
                                              Bundle Requirements
                                              (RequiresBundleWith)
                                            </FieldLabel>
                                            {bundleEntries.map((entry, idx) => (
                                              <div
                                                key={idx}
                                                className="d-flex gap-2 align-items-start mb-1"
                                              >
                                                <BundleKeyPicker
                                                  value={entry.canonicalKey}
                                                  domain={domain}
                                                  onChange={(key) => {
                                                    const updated = [
                                                      ...bundleEntries,
                                                    ];
                                                    updated[idx] = {
                                                      ...updated[idx],
                                                      canonicalKey: key,
                                                    };
                                                    setBundleEntries(updated);
                                                  }}
                                                />
                                                <Form.Control
                                                  size="sm"
                                                  type="number"
                                                  min={1}
                                                  placeholder="Min qty"
                                                  value={
                                                    entry.minimumQuantity ?? ""
                                                  }
                                                  onChange={(e) => {
                                                    const updated = [
                                                      ...bundleEntries,
                                                    ];
                                                    updated[idx] = {
                                                      ...updated[idx],
                                                      minimumQuantity: e.target
                                                        .value
                                                        ? Number(e.target.value)
                                                        : null,
                                                    };
                                                    setBundleEntries(updated);
                                                  }}
                                                  style={{ flex: 1 }}
                                                />
                                                <Form.Control
                                                  size="sm"
                                                  type="number"
                                                  min={0}
                                                  step={0.01}
                                                  placeholder="Min spend"
                                                  value={
                                                    entry.minimumSpend ?? ""
                                                  }
                                                  onChange={(e) => {
                                                    const updated = [
                                                      ...bundleEntries,
                                                    ];
                                                    updated[idx] = {
                                                      ...updated[idx],
                                                      minimumSpend: e.target
                                                        .value
                                                        ? Number(e.target.value)
                                                        : null,
                                                    };
                                                    setBundleEntries(updated);
                                                  }}
                                                  style={{ flex: 1 }}
                                                />
                                                <Button
                                                  size="sm"
                                                  variant="outline-danger"
                                                  onClick={() => {
                                                    setBundleEntries(
                                                      bundleEntries.filter(
                                                        (_, i) => i !== idx
                                                      )
                                                    );
                                                  }}
                                                >
                                                  <X size={12} />
                                                </Button>
                                              </div>
                                            ))}
                                            <Button
                                              size="sm"
                                              variant="outline-secondary"
                                              onClick={() =>
                                                setBundleEntries([
                                                  ...bundleEntries,
                                                  { canonicalKey: "" },
                                                ])
                                              }
                                            >
                                              <Plus
                                                size={12}
                                                className="me-1"
                                              />
                                              Add Bundle Requirement
                                            </Button>
                                          </div>
                                          <div className="col-12 d-flex align-items-end gap-1 mt-2">
                                            <Button
                                              size="sm"
                                              variant="primary"
                                              onClick={() =>
                                                handleSaveConditions(target)
                                              }
                                              disabled={updateTargetPut.loading}
                                            >
                                              <Save
                                                size={12}
                                                className="me-1"
                                              />
                                              Save
                                            </Button>
                                            <Button
                                              size="sm"
                                              variant="outline-secondary"
                                              onClick={() =>
                                                setEditingConditionsId(null)
                                              }
                                            >
                                              Cancel
                                            </Button>
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </Table>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            )}
          </div>
        </div>
      </div>
    </fieldset>
  );
};
